import React from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, EyeOff, ArrowLeftRight, Layers } from "lucide-react";
import CoinIcon from "@/components/landing/CoinIcon";

const CONFIG = [
    { icon: Eye, cost: 3, kind: "reveal" },
    { icon: EyeOff, cost: 4, kind: "reveal" },
    { icon: ArrowLeftRight, cost: 6, kind: "score" },
    { icon: ArrowLeftRight, cost: 6, kind: "score" },
];

export default function PowerUps() {
    const reduce = useReducedMotion();
    const { t } = useTranslation();
    const items = t("powerups.items", { returnObjects: true });
    const safeItems = Array.isArray(items) ? items : [];
    return (
        <section id="powerups" className="py-16 sm:py-20 lg:py-24 bg-[#F4EBDD]" data-testid="powerups-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="flex flex-col items-start gap-4 max-w-2xl">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#FBF7EE] border border-[#E9D9C1] px-3 py-1 text-xs font-semibold text-[#0B4A34] uppercase tracking-wider">{t("powerups.eyebrow")}</span>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#062A1E]">{t("powerups.title")}</h2>
                    <p className="text-[#1B2621]/75 text-base sm:text-lg">
                        {t("powerups.subtitle_lead")}{" "}
                        <span className="font-semibold text-[#0B4A34]">{t("powerups.subtitle_loadout")}</span>
                        {t("powerups.subtitle_tail")}
                    </p>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    {safeItems.map((p, idx) => {
                        const cfg = CONFIG[idx] || CONFIG[0];
                        const Icon = cfg.icon;
                        return (
                            <motion.div key={idx} initial={reduce ? false : { opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, delay: idx * 0.06 }} className="rounded-2xl border border-[#E9D9C1] bg-[#FBF7EE] p-5 sm:p-6 shadow-[0_10px_30px_rgba(6,42,30,0.06)] hover:shadow-[0_18px_50px_rgba(6,42,30,0.12)] hover:-translate-y-0.5 transition-shadow transition-transform duration-200" data-testid={`powerup-card-${idx}`}>
                                <div className="flex items-start gap-4">
                                    <span className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-[#0B4A34] to-[#137A55] text-[#FBF7EE] shadow-[0_8px_20px_rgba(6,42,30,0.25)] flex-shrink-0"><Icon className="h-6 w-6" /></span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-3">
                                            <h3 className="text-lg font-bold text-[#062A1E]">{p.name}</h3>
                                            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase font-bold text-[#8B6F2A] bg-[#FFF3D6] border border-[#E9D9C1] rounded-full px-2.5 py-0.5 whitespace-nowrap"><CoinIcon className="h-3.5 w-3.5" />{t("powerups.coinsLabel", { count: cfg.cost })}</span>
                                        </div>
                                        <p className="mt-1.5 text-sm text-[#1B2621]/80 leading-relaxed">{p.detail}</p>
                                        <div className="mt-3"><span className="text-[10px] tracking-wider uppercase font-semibold text-[#5B6B63]">{p.kind}</span></div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
                <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45 }} className="mt-5 rounded-2xl border border-[#E9D9C1] bg-[#FBF7EE]/60 p-5 flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#F4EBDD] border border-[#E9D9C1] text-[#8B6F2A] flex-shrink-0"><Layers className="h-5 w-5" /></span>
                    <div>
                        <div className="font-semibold text-[#062A1E]">{t("powerups.mixStackTitle")}</div>
                        <p className="text-sm text-[#1B2621]/75 mt-0.5">{t("powerups.mixStackBody")}</p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
